import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UploadSimple, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Reusable file uploader backed by /api/storage/upload.
 * Props:
 *   - kind: string tag saved with the file (e.g. "product_image", "delivery_proof")
 *   - refType/refId: optional entity link ("product"/<id>, "order"/<id>, ...)
 *   - accept: HTML accept string (e.g. "image/*,application/pdf")
 *   - label: field label shown above the input
 *   - onUploaded: callback(fileRecord) after a successful upload
 *   - testId: data-testid base for the input+button
 */
export function FileUploader({
  kind = "misc",
  refType = null,
  refId = null,
  accept = "image/*,application/pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  label = "Attach file",
  onUploaded,
  testId = "file-uploader",
}) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    if (refType) fd.append("ref_type", refType);
    if (refId) fd.append("ref_id", refId);
    try {
      const { data } = await api.post("/storage/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded");
      setFile(null);
      onUploaded?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row gap-3 items-end" data-testid={testId}>
      <div className="flex-1">
        <Label className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</Label>
        <Input
          type="file"
          accept={accept}
          onChange={(e) => setFile(e.target.files[0])}
          className="border-zinc-300 rounded-md mt-1"
          data-testid={`${testId}-input`}
        />
      </div>
      <Button
        onClick={upload}
        disabled={busy || !file}
        className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md"
        data-testid={`${testId}-button`}
      >
        {busy ? (
          <CircleNotch size={16} className="animate-spin mr-1" />
        ) : (
          <UploadSimple size={16} weight="bold" className="mr-1" />
        )}
        Upload
      </Button>
    </div>
  );
}

/** Absolute URL for a storage download reference. */
export function fileUrl(downloadUrl, { inline = true } = {}) {
  if (!downloadUrl) return "";
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const sep = downloadUrl.includes("?") ? "&" : "?";
  return `${base}${downloadUrl}${inline ? "" : `${sep}inline=0`}`;
}
